import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { ExpenseCategory } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Expense Categories',
    description: 'Manage expense categories',
    permissionModuleKey: 'expense_categories',
    route: {
      list: '/p_crm_expense_category',
      new: '/p_crm_expense_category/new',
      detail: (id: number) => `/p_crm_expense_category/${id}`,
      edit: (id: number) => `/p_crm_expense_category/${id}/edit`,
    },
    api: {
      list: '/crm-expense-category',
      detail: (id: number) => `/crm-expense-category/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search expense categories...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter name...',
        type: 'text' as const,
        getValue: (item: ExpenseCategory) => item.name,
        options: [],
      },
    ],
    applyFilter: (items: ExpenseCategory[], filters: { name: string[] | string }) => {
      return items.filter((item) => {
        const nameFilter = filters.name;
        if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(nameFilter.toLowerCase())) return false;
        } else if (Array.isArray(nameFilter) && nameFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = nameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Expense Category',
        showTitle: false,
        variant: 'primary' as const,
        href: '/p_crm_expense_category/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: ExpenseCategory) => {
      window.location.href = `/p_crm_expense_category/${item.id}`;
    },
    keyExtractor: (item: ExpenseCategory) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new expense category.',
    noSearchResults: 'No expense categories found matching your search criteria.',
    noFilterResults: 'No expense categories match the selected filters.',
  },
  delete: {
    title: 'Delete Expense Category',
    message: 'Are you sure you want to delete this expense category? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete expense category. Please try again.',
    handler: async (id: number) => {
      const { deleteExpenseCategory } = await import('../../../services/mutations');
      await deleteExpenseCategory(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Expense Category Details',
    notFoundTitle: 'Expense Category not found',
    notFoundMessage: 'The expense category you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Expense Categories',
    defaultTitle: 'Unnamed Expense Category',
    getTitle: (item: ExpenseCategory) => {
      return item.name || `Expense Category #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Expense Category',
      subtitle: 'Add a new expense category',
      submitText: 'Create Expense Category',
    },
    edit: {
      title: 'Edit Expense Category',
      subtitle: 'Update expense category information',
      submitText: 'Update Expense Category',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading expense category...',
    errorMessage: 'Failed to save expense category. Please try again.',
    create: async (data: Partial<ExpenseCategory>) => {
      const { createExpenseCategory } = await import('../../../services/mutations');
      return await createExpenseCategory(data);
    },
    update: async (id: number, data: Partial<ExpenseCategory>) => {
      const { updateExpenseCategory } = await import('../../../services/mutations');
      return await updateExpenseCategory(id, data);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter expense category name',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Expense Category Information',
        icon: '📝',
        fields: ['name'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<ExpenseCategory>[] => [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value) => (
      value || '-'
    ),
  },
];

export const getDetailSections = (
  item: ExpenseCategory,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Name',
          value: item.name || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: ExpenseCategory) => {
  return null;
};

export const getInitialFormData = (): Partial<ExpenseCategory> => {
  return {
    name: '',
  };
};

export const mapItemToFormData = (item: ExpenseCategory): Partial<ExpenseCategory> => {
  return {
    name: item.name || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: ExpenseCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

